// Operation Health — compact list badge (Step 17G).
//
// Deliberately kept in its own tiny module, separate from OperationsHub/
// index.js: that file transitively imports "../Generic" (icons, tooltips,
// modals...), which in this project's current Jest setup cannot be imported
// in tests at all (a pre-existing, unrelated chain of environment gaps —
// @lottiefiles/react-lottie-player calling into an unimplemented jsdom canvas
// API, and this project's narrowed `transformIgnorePatterns` in package.json
// blocking the default CSS transform for third-party stylesheets under
// node_modules — reproducible on the untouched baseline via src/App.test.js).
// Splitting this pure mapping out keeps it independently, deterministically
// testable without touching package.json or any global Jest config.
import { resolveOperationHealthPresentation, OPERATION_HEALTH_STATUS } from "../../services/operationHealth";

// Backend `health_status` is the single source of truth for status/severity
// (services/operations/health/operationHealth.js in wine2trace-server) — this
// table only assigns an icon/color per already-decided status, it never
// re-derives or re-ranks anything.
export const OPERATION_HEALTH_VISUALS = Object.freeze({
  VALID: { icon: "fa-check-circle", color: "#2e7d32" },
  WARNING: { icon: "fa-triangle-exclamation", color: "#b7791f" },
  BLOCKED: { icon: "fa-ban", color: "#c0392b" },
  STALE: { icon: "fa-rotate", color: "#2980b9" },
  UNKNOWN: { icon: "fa-circle-info", color: "#7f8c8d" },
});

/**
 * Pure helper: computes the compact Operation Health badge for one operation
 * row, or `null` when nothing should be shown (not-applicable, or an
 * unmigrated read path with no legacy signal either).
 *
 * @param {object} operation - the operation row (Redux `operations` entry)
 * @param {Function} getLabel - i18n resolver (operationHealth* keys, IT/EN)
 * @returns {{icon: string, color: string, text: string, title: string}|null}
 */
export function getOperationHealthBadge(operation, getLabel) {
  const presentation = resolveOperationHealthPresentation(operation, {});

  if (!presentation.titleKey) {
    // Not-applicable, or the (currently unreachable in real usage — see the
    // Step 17G report) legacy fallback with nothing to say: no list noise.
    return null;
  }
  if (presentation.status === OPERATION_HEALTH_STATUS.NOT_APPLICABLE) {
    return null;
  }

  const visual = OPERATION_HEALTH_VISUALS[presentation.status] || OPERATION_HEALTH_VISUALS.UNKNOWN;
  const text = getLabel(presentation.titleKey);
  const reasonSummary = presentation.reasonItems.length > 0
    ? presentation.reasonItems.map((r) => getLabel(r.labelKey)).join(" ")
    : getLabel(presentation.descriptionKey);

  return {
    icon: visual.icon,
    color: visual.color,
    text,
    title: reasonSummary,
  };
}
